import React, { useState, useEffect, useRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { FaGlobe, FaRegEdit } from "react-icons/fa"
import { useLocation, useNavigate } from "react-router-dom"
import { routesMap } from "../routesMap"

const languages = [
  { code: "en", lang: "English" },
  { code: "it", lang: "Italian" },
]

const getLocalizedPath = (path, language) => {
  const normalizedPath = path.replace(/^\/(en|it)/, "") || "/"

  const reverseMap = {}
  Object.entries(routesMap).forEach(([baseRoute, translations]) => {
    reverseMap[translations.it] = baseRoute
    reverseMap[translations.en.replace("/en", "")] = baseRoute
    reverseMap[baseRoute] = baseRoute
  })

  const baseRoute = reverseMap[normalizedPath]

  if (!baseRoute) {
    return language === "en" ? `/en${normalizedPath}` : normalizedPath
  }

  return routesMap[baseRoute][language]
}

const LanguageSelector = ({ editorEnabled = false }) => {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()

  const EDIT_PASSWORD = "justtomakesure"
  const STORAGE_KEY = "translatorAuth"
  const EXPIRATION_DAYS = 30

  const checkAuthStatus = () => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return false

    try {
      const { expiry } = JSON.parse(stored)
      if (new Date().getTime() > expiry) {
        localStorage.removeItem(STORAGE_KEY)
        return false
      }
      return true
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY)
      return false
    }
  }

  const setAuthStatus = () => {
    const expiryDate = new Date()
    expiryDate.setDate(expiryDate.getDate() + EXPIRATION_DAYS)
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        authenticated: true,
        expiry: expiryDate.getTime(),
      })
    )
  }

  const toggleEditMode = () => {
    if (window.location.hash === "#translate") {
      window.history.replaceState(null, null, " ")
    } else {
      const isAuthenticated = checkAuthStatus()

      if (isAuthenticated) {
        window.location.hash = "#translate"
      } else {
        const password = prompt("Enter password to enable translation editor:")
        if (password === EDIT_PASSWORD) {
          setAuthStatus()
          window.location.hash = "#translate"
        } else {
          alert("Incorrect password. Access denied.")
        }
      }
    }
  }

  // Close dropdown when clicking outside
  const handleClickOutside = useCallback((e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setOpen(false)
    }
  }, [])

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("touchstart", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("touchstart", handleClickOutside)
    }
  }, [handleClickOutside])

  // Effect to handle initial language based on URL
  useEffect(() => {
    const path = location.pathname
    const shouldBeEnglish = path.startsWith("/en")
    if (shouldBeEnglish && i18n.language !== "en") {
      i18n.changeLanguage("en")
    } else if (!shouldBeEnglish && i18n.language !== "it") {
      i18n.changeLanguage("it")
    }
  }, [location.pathname, i18n])

  const changeLanguage = async (lng) => {
    if (lng === i18n.language) {
      setOpen(false)
      return
    }

    const currentPath = location.pathname
    const newPath = getLocalizedPath(currentPath, lng)

    await i18n.changeLanguage(lng)

    navigate(newPath, { replace: true })
    setOpen(false)
  }

  const toggleDropdown = () => setOpen((prev) => !prev)

  return (
    <div className="relative z-40 flex" ref={dropdownRef}>
      {editorEnabled && (
        <button
          onClick={toggleEditMode}
          className="ml-4 px-2 py-1 mr-2 bg-gray-200 rounded"
        >
          {window.location.hash === "#translate" ? "X" : <FaRegEdit />}
        </button>
      )}
      <button
        className="focus:outline-none flex items-center gap-0"
        onClick={toggleDropdown}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <FaGlobe className="mr-2" />
        {languages.find((lng) => lng.code === i18n.language)?.lang}
      </button>
      <div
        className={`absolute top-full bg-background-light right-0 shadow z-40 transition-transform-opacity duration-500 ease-custom-easing transform ${
          open
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {languages
          .filter((lng) => lng.code !== i18n.language)
          .map((lng) => (
            <div
              key={lng.code}
              className="w-full text-left text-primary-400 hover:text-white hover:bg-secondary-400 py-1 px-2 cursor-pointer"
              onClick={() => changeLanguage(lng.code)}
            >
              {lng.lang}
            </div>
          ))}
      </div>
    </div>
  )
}

export default LanguageSelector
